import React, { useEffect, useState } from 'react';
import { welcomeApi, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Upload, Save, PartyPopper } from 'lucide-react';

const VARIANTS = [['consumer', 'Consumer welcome'], ['owner', 'Business owner welcome']];

const VariantEditor = ({ k, label, v, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const set = (f) => (e) => onChange({ ...v, [f]: e.target.value });
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const r = await filesApi.upload(f, 'post'); onChange({ ...v, imageUrl: r.url }); toast({ title: 'Image uploaded' }); }
    catch (err) { toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' }); }
    setUploading(false); e.target.value = '';
  };
  return (
    <div className="pbb-card p-5 space-y-3" data-testid={`welcome-editor-${k}`}>
      <div className="font-semibold text-white">{label}</div>
      <label className="block text-xs text-gray-300">Title<input className="pbb-input mt-1" value={v.title} onChange={set('title')} data-testid={`welcome-${k}-title`}/></label>
      <label className="block text-xs text-gray-300">Message<textarea rows={8} className="pbb-input mt-1" value={v.body} onChange={set('body')} data-testid={`welcome-${k}-body`}/></label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block text-xs text-gray-300">Button label<input className="pbb-input mt-1" value={v.ctaLabel} onChange={set('ctaLabel')} data-testid={`welcome-${k}-cta-label`}/></label>
        <label className="block text-xs text-gray-300">Button link (path)<input className="pbb-input mt-1" value={v.ctaPath} onChange={set('ctaPath')} data-testid={`welcome-${k}-cta-path`}/></label>
      </div>
      <label className="block text-xs text-gray-300">Optional video link (YouTube or .mp4 — replaces the image when set)
        <input className="pbb-input mt-1" placeholder="https://youtube.com/watch?v=…" value={v.videoUrl} onChange={set('videoUrl')} data-testid={`welcome-${k}-video`}/>
      </label>
      <div className="flex items-center gap-3">
        {v.imageUrl && <img src={filesApi.urlFor(v.imageUrl)} alt="" className="w-32 aspect-[3/2] object-cover rounded-lg border border-[#2a2a2a]" data-testid={`welcome-${k}-image-preview`}/>}
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2a2a2a] text-sm text-gray-200 cursor-pointer hover:border-green-600/60">
          {uploading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>} Swap image
          <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`welcome-${k}-image-upload`}/>
        </label>
      </div>
    </div>
  );
};

export default function AdminWelcomeEditor() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { welcomeApi.get().then(setCfg); }, []);
  const save = async () => {
    setSaving(true);
    try { setCfg(await welcomeApi.save(cfg)); toast({ title: 'Welcome messages saved' }); }
    catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail?.[0]?.msg || 'Check required fields' }); }
    setSaving(false);
  };
  if (!cfg) return <div className="flex items-center gap-2 text-gray-300"><Loader2 size={14} className="animate-spin"/> Loading…</div>;
  return (
    <div className="space-y-5" data-testid="admin-welcome-editor">
      <div className="flex items-center gap-3 flex-wrap">
        <PartyPopper className="text-yellow-400" size={20}/>
        <div>
          <div className="font-semibold text-white">Welcome Message Editor</div>
          <div className="text-xs text-gray-300">Shown once, with confetti, the first time a new member logs in.</div>
        </div>
        <button onClick={save} disabled={saving} data-testid="welcome-save"
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
        </button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {VARIANTS.map(([k, label]) => <VariantEditor key={k} k={k} label={label} v={cfg[k]} onChange={(nv) => setCfg({ ...cfg, [k]: nv })}/>)}
      </div>
    </div>
  );
}
